import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { fetchDonationTarget } from "../Donations/donationEngineApi";
import { fetchOrganizationBySlug } from "../Helper/organizations";
import {
  entityPublicPath,
  entityDonatePath,
} from "../Helper/entityRoutes";
import { AppearanceButton } from "../Appearance";
import EntityIdentity from "../Helper/EntityIdentity";
import { buildEntityBadges } from "../Helper/EntityBadges";
import {
  resolveMediaUrl,
  handleMediaImgError,
} from "../Helper/mediaUrl";
import EmptyState from "../Dashboard/EmptyState";
import "./EntityDonatePage.css";
import "./Donate.css";

function resolveEntity(match, location) {
  const p = (match && match.params) || {};
  let entityType = (p.entityType || "").toLowerCase();
  let slug = p.slug || "";

  if (!entityType && location && location.pathname) {
    const parts = location.pathname.replace(/^\//, "").split("/");
    if (parts.length >= 2) {
      const map = {
        organizations: "organization",
        organization: "organization",
        org: "organization",
        creators: "creator",
        creator: "creator",
        projects: "project",
        project: "project",
      };
      entityType = map[parts[0]] || parts[0];
      slug = parts[1];
    }
  }
  if (entityType === "org") entityType = "organization";
  return { entityType: entityType || "organization", slug };
}

function DonateThankYouPage({ match, location }) {
  const { entityType, slug } = useMemo(
    () => resolveEntity(match, location),
    [match, location]
  );
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [logo, setLogo] = useState("");
  const [handle, setHandle] = useState(slug);
  const [badges, setBadges] = useState([]);
  const [typeLabel, setTypeLabel] = useState("");
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const stored = sessionStorage.getItem("donate_thank_you_amount");
        if (stored) setAmount(stored);
      } catch (e) {
        /* ignore */
      }
      const target = await fetchDonationTarget(entityType, slug);
      if (cancelled) return;
      let nextName = "";
      let nextLogo = "";
      let nextHandle = slug;
      let nextBadges = [];
      let nextType =
        entityType === "creator"
          ? "Creator"
          : entityType === "project"
          ? "Project"
          : "Organization";
      if (target && target.found !== false) {
        const branding = target.branding || {};
        nextName = branding.title || target.title || "";
        nextLogo = branding.logo || target.logo || "";
        nextHandle = target.slug || slug;
      }
      if (entityType === "organization" && nextHandle) {
        const org = await fetchOrganizationBySlug(nextHandle);
        if (org) {
          nextBadges = buildEntityBadges(org);
          if (!nextLogo && org.logo_path) nextLogo = org.logo_path;
          if (!nextName && org.name) nextName = org.name;
          nextType =
            org.organization_type_label ||
            org.type_label ||
            org.organization_type ||
            nextType;
        }
      }
      if (!cancelled) {
        setName(nextName || "Community partner");
        setLogo(nextLogo);
        setHandle(nextHandle);
        setBadges(nextBadges);
        setTypeLabel(nextType);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entityType, slug]);

  const backPath = entityPublicPath(entityType, handle || slug);
  const noun =
    entityType === "creator"
      ? "Creator"
      : entityType === "project"
      ? "Project"
      : "Organization";
  const logoSrc = logo ? resolveMediaUrl(logo) : "";

  if (loading) {
    return (
      <div className="main-sec-content entity-donate-page" role="status">
        <EmptyState title="Loading…" message="Confirming your donation." />
      </div>
    );
  }

  return (
    <div className="donate-feature-sec donate-feature-sec--branded main padding-top-md">
      <Helmet>
        <title>{`Thank you — ${name}`}</title>
      </Helmet>
      <div className="top-bottom-spacing resp-align-center">
        <div className="donate-thanks-card">
          <div className="donate-checkout-identity">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={name}
                className="org-logo"
                onError={handleMediaImgError}
              />
            ) : (
              <div
                className="org-logo-placeholder"
                role="img"
                aria-label={name}
              >
                <i
                  className={
                    entityType === "creator"
                      ? "fas fa-user"
                      : entityType === "project"
                      ? "fas fa-folder-open"
                      : "fas fa-building"
                  }
                  aria-hidden="true"
                />
              </div>
            )}
            <EntityIdentity
              name={name}
              handle={handle}
              typeLabel={typeLabel}
              badges={badges}
              variant="checkout"
            />
          </div>
          <h2 className="donate-thanks-title">
            Thank you for supporting {name}
          </h2>
          {amount != null ? (
            <p className="donate-thanks-amount">
              Donation confirmation · ${amount}
            </p>
          ) : (
            <p className="donate-thanks-amount">
              Your donation has been received.
            </p>
          )}
          <p className="donate-checkout-identity__powered">
            Securely processed by Pentecost+
          </p>
          <AppearanceButton
            to={backPath || entityDonatePath(entityType, handle || slug)}
            variant="primary"
            className="donate-thanks-return"
          >
            Return to {noun}
          </AppearanceButton>
        </div>
      </div>
    </div>
  );
}

export default DonateThankYouPage;
